/**
 * Web implementation of the contract file upload zone.
 *
 * Renders a styled drop zone that accepts both HTML5 drag-and-drop and
 * `<input type="file">` selection. Adapts each picked file into a
 * {@link StagedContractFile} (preserving the underlying `File` object
 * via `webFile` so the upload hook can PUT it directly to S3 without
 * re-reading the bytes).
 * @module features/contracts/components/FileUploadZone/FileUploadZoneView.web
 */

import { Upload } from "lucide-react-native";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";

import type {
  ContractDocumentType,
  StagedContractFile,
} from "@/features/contracts/types";

import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props the platform views agree on (set by the container). */
interface Props {
  readonly disabled: boolean;
  readonly multiple: boolean;
  /**
   * Active document type. Drives the drop-zone heading so the wording
   * matches the enclosing section header (Contract vs Transfer
   * Agreement). Optional; defaults to the contract phrasing so existing
   * call sites that have not yet threaded the prop keep the pre-SE-4605
   * behaviour.
   */
  readonly documentType?: ContractDocumentType;
  readonly acceptedMimeTypes: readonly string[];
  readonly onFilesPicked: (files: readonly StagedContractFile[]) => void;
}

const UPLOAD_ZONE_TEST_ID = "file-upload-zone";
const FILE_INPUT_TEST_ID = "file-upload-input";
const TRIGGER_TEST_ID = "file-upload-trigger";

/**
 * Adapts a browser `File` into a {@link StagedContractFile}.
 * @param file - The picked browser File
 * @returns The staged-contract-file representation
 */
function toStagedFile(file: File): StagedContractFile {
  return {
    id: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    type: file.type,
    webFile: file,
  };
}

/**
 * Web file upload zone with HTML5 drag-and-drop + a hidden file input.
 *
 * The outer wrapper carries only drag event handlers and is marked
 * `role="none"` so assistive technology ignores it as a non-interactive
 * container. All keyboard and pointer activation flows through the inner
 * `Button` "Choose files" control, which is the single interactive
 * control and the only entry point for screen-reader users.
 * @param props - Platform view props
 * @param props.disabled - Whether the zone should ignore input
 * @param props.multiple - Whether the file input should accept multiple files
 * @param props.documentType - Active document type; drives drop-zone heading phrasing
 * @param props.acceptedMimeTypes - MIME types listed in the input's `accept` attribute
 * @param props.onFilesPicked - Invoked with the picked files
 * @returns The rendered drop zone
 */
function FileUploadZoneViewWeb({
  disabled,
  multiple,
  documentType = "CONTRACT",
  acceptedMimeTypes,
  onFilesPicked,
}: Props) {
  // Single-switch subcopy lookup — keep both strings inline so future
  // readers see all drop-zone phrasings at a glance rather than hunting
  // through an i18n dictionary. Over-engineering this into a key map
  // would add indirection for two strings.
  const assetLabel =
    documentType === "TRANSFER_AGREEMENT"
      ? "transfer agreement documents"
      : "contract documents";
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Tracks nested dragenter/dragleave pairs so that hovering over a child
  // element (e.g. the "Choose files" button) does not prematurely clear the
  // active highlight. Counter reaches 0 only when the drag truly exits.
  const dragCounter = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  const acceptAttribute = useMemo(
    () => acceptedMimeTypes.join(","),
    [acceptedMimeTypes]
  );

  const emitFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList).map(toStagedFile);
      if (files.length > 0) {
        onFilesPicked(files);
      }
    },
    [onFilesPicked]
  );

  const handleBrowseClick = useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.target;
      emitFiles(target.files);
      // Reset the input so re-selecting the same file fires onChange again.
      target.value = "";
    },
    [emitFiles]
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!disabled) {
        dragCounter.current += 1;
        if (dragCounter.current === 1) {
          setIsDragActive(true);
        }
      }
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) {
        setIsDragActive(false);
      }
    },
    []
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragCounter.current = 0;
      setIsDragActive(false);
      if (disabled) return;
      emitFiles(event.dataTransfer?.files ?? null);
    },
    [disabled, emitFiles]
  );

  return (
    // role="none" marks this as a presentational drag target so assistive
    // technology ignores it. jsx-a11y exempts role="none" elements from the
    // no-static-element-interactions rule. The Button below is the sole
    // interactive control — this eliminates nested button semantics that caused
    // the SonarCloud warning (AZ1zHpyq…) and potential double-activation via
    // event bubbling.
    <div
      role="none"
      data-testid={UPLOAD_ZONE_TEST_ID}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`rounded-sm border-2 border-dashed p-8 ${
        isDragActive
          ? "border-needs-semantic-primary-500 bg-needs-semantic-primary-50"
          : "border-surface-strong bg-surface-raised"
      } ${disabled ? "opacity-50" : ""}`}
    >
      <Stack className="items-center gap-3">
        <Icon as={Upload} size="xl" className="text-content-muted" />
        <Heading
          variant="title-sm"
          className="text-center text-content-primary"
        >
          {`Drag and drop your ${assetLabel} here`}
        </Heading>
        <Text variant="body" className="text-center text-content-muted">
          or
        </Text>
        <Button
          testID={TRIGGER_TEST_ID}
          onPress={handleBrowseClick}
          isDisabled={disabled}
          tone="primary"
          variant="solid"
          size="sm"
          label="Choose files"
        />
        <Text variant="caption" className="text-content-muted">
          PDF, JPEG, or PNG
        </Text>
      </Stack>
      {/*
        Hidden native file input. We render it inside the drop zone so
        Playwright can find it via setInputFiles and so the file dialog
        opens when the styled "Choose files" button is pressed.
      */}
      <input
        ref={inputRef}
        type="file"
        data-testid={FILE_INPUT_TEST_ID}
        accept={acceptAttribute}
        multiple={multiple}
        disabled={disabled}
        onChange={handleInputChange}
        className="hidden"
      />
    </div>
  );
}

FileUploadZoneViewWeb.displayName = "FileUploadZoneView";

export default memo(FileUploadZoneViewWeb);
